import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Settings, Download, Upload, Trash2, CalendarIcon, Lock, Eye, EyeOff } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";

export default function CycleSettings() {
  const navigate = useNavigate();
  const { cycleConfig, updateCycleConfig, exportData, importData, changeAppPassword, cycleKeys } = useApp();
  
  const [startDate, setStartDate] = useState(
    cycleConfig.customStart ? new Date(cycleConfig.customStart) : new Date()
  );
  const [endDate, setEndDate] = useState(
    cycleConfig.customEnd ? new Date(cycleConfig.customEnd) : new Date()
  );

  // Password change state
  const [masterPassword, setMasterPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showMasterPass, setShowMasterPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [masterVerified, setMasterVerified] = useState(false);

  const handleSave = () => {
    if (startDate >= endDate) {
      toast.error("End date must be after start date");
      return;
    }
    
    const startISO = format(startDate, "yyyy-MM-dd");
    const endISO = format(endDate, "yyyy-MM-dd");
    
    updateCycleConfig({ 
      customStart: startISO,
      customEnd: endISO,
      startDay: startDate.getDate(),
      endDay: endDate.getDate()
    });
    
    toast.success("Budget cycle saved!");
  };

  const handleClearData = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleVerifyMaster = () => {
    if (masterPassword === "Anita@Birendra") {
      setMasterVerified(true);
      toast.success("Master password verified! You can now set a new password.");
    } else {
      toast.error("Incorrect master password");
      setMasterPassword("");
    }
  };

  const handleChangePassword = () => {
    if (!masterVerified) {
      toast.error("Please verify master password first");
      return;
    }
    if (newPassword.length < 4) {
      toast.error("New password must be at least 4 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const result = changeAppPassword("Anita@Birendra", newPassword);
    if (result.success) {
      toast.success("Password changed successfully!");
      setMasterPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMasterVerified(false);
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cycle-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-stone-100">
            <Settings className="w-6 h-6 text-stone-600" />
          </div>
          <div>
            <h2 className="font-heading text-3xl text-stone-900">Settings</h2>
            <p className="text-stone-500">Configure your app</p>
          </div>
        </div>
      </div>

      {/* Password Change Section */}
      <Card className="p-6 bg-white rounded-2xl border border-stone-200/60">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-amber-100">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="font-semibold text-stone-900">Change App Password</h3>
        </div>
        <p className="text-sm text-stone-500 mb-6">
          Enter the master password to unlock password change.
        </p>
        
        <div className="space-y-4">
          {/* Master Password */}
          <div className="space-y-2">
            <Label className="text-stone-700">Master Password</Label>
            <div className="relative">
              <Input 
                type={showMasterPass ? "text" : "password"}
                placeholder="Enter master password"
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
                disabled={masterVerified}
                className={masterVerified ? "bg-emerald-50 border-emerald-300" : ""}
              />
              <button 
                type="button"
                onClick={() => setShowMasterPass(!showMasterPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showMasterPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!masterVerified && (
              <Button 
                onClick={handleVerifyMaster} 
                variant="outline" 
                className="mt-2 rounded-full"
                disabled={!masterPassword}
              >
                Verify Master Password
              </Button>
            )}
            {masterVerified && (
              <p className="text-sm text-emerald-600 font-medium">✓ Master password verified</p>
            )}
          </div>

          {/* New Password - Only shown after master is verified */}
          {masterVerified && (
            <>
              <div className="space-y-2">
                <Label className="text-stone-700">New Password</Label>
                <div className="relative">
                  <Input 
                    type={showNewPass ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button 
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Confirm New Password</Label>
                <Input 
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <Button 
                onClick={handleChangePassword} 
                className="rounded-full bg-stone-900 hover:bg-stone-800"
                disabled={!newPassword || !confirmPassword}
              >
                Change Password
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Cycle Configuration */}
      <Card className="p-6 bg-white rounded-2xl border border-stone-200/60">
        <h3 className="font-semibold text-stone-900 mb-4">Set Your Budget Cycle</h3>
        <p className="text-sm text-stone-500 mb-6">
          Choose any custom date range for your budget cycle.
        </p>
        
        <div className="space-y-6">
          {/* Start Date */}
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <Label className="text-emerald-800 font-semibold mb-3 block">Cycle Starts</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal bg-white">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(startDate, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => date && setStartDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
            <Label className="text-rose-800 font-semibold mb-3 block">Cycle Ends</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal bg-white">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(endDate, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => date && setEndDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
          <p className="text-sm text-indigo-800">
            <strong>Your cycle:</strong> {format(startDate, "MMMM d, yyyy")} → {format(endDate, "MMMM d, yyyy")}
          </p>
        </div>

        <Button onClick={handleSave} className="mt-6 rounded-full bg-stone-900 hover:bg-stone-800" data-testid="save-cycle-btn">
          Save Cycle Settings
        </Button>
      </Card>

      {/* Data Management */}
      <Card className="p-6 bg-white rounded-2xl border border-stone-200/60">
        <h3 className="font-semibold text-stone-900 mb-4">Data Management</h3>
        <p className="text-sm text-stone-500 mb-6">
          Export your data as JSON backup, import from a backup file, or reset with sample data.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={exportData} className="rounded-full" data-testid="export-data-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
          
          <Button variant="outline" onClick={importData} className="rounded-full" data-testid="import-data-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="clear-data-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear All Data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all your categories, transactions, and bills. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearData} className="bg-rose-600 hover:bg-rose-700">
                  Clear All Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>

      {/* Info */}
      <Card className="p-6 bg-stone-50 rounded-2xl border border-stone-200">
        <h3 className="font-semibold text-stone-900 mb-2">How It Works</h3>
        <p className="text-sm text-stone-600">
          Set your budget cycle to any date range you want. All your transactions and bills will be tracked within this period. 
          Your data is stored locally in your browser - remember to export backups regularly!
        </p>
      </Card>
    </div>
  );
}
